import React, { createContext, useState, useCallback, useLayoutEffect } from 'react';

/**
 * SidebarContext provides the expanded/collapsed state of the sidebar
 * to all child components so they can adjust their layout accordingly
 */
export const SidebarContext = createContext();

const SIDEBAR_WIDTH_COLLAPSED = 72;
const SIDEBAR_WIDTH_EXPANDED = 300;

export const SidebarProvider = ({ children }) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false); // Default: collapsed
  const [isSidebarHoverExpanded, setIsSidebarHoverExpanded] = useState(false);

  const updateSidebarState = useCallback((expanded, hoverExpanded = false) => {
    setIsSidebarExpanded(expanded);
    setIsSidebarHoverExpanded(hoverExpanded);
  }, []);

  const sidebarWidth = isSidebarExpanded || isSidebarHoverExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  // Set --sidebar-width on root before paint so layout (e.g. /landing) is correct on first load after login
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
  }, [sidebarWidth]);

  const value = {
    isSidebarExpanded,
    isSidebarHoverExpanded,
    isAnySidebarStateExpanded: isSidebarExpanded || isSidebarHoverExpanded,
    updateSidebarState,
    // Calculate sidebar width based on state
    sidebarWidth: isSidebarExpanded || isSidebarHoverExpanded ? 320 : 72,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarContext = () => {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
};
